import { useCallback, useMemo, useState } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DateRangeOutlined from "@mui/icons-material/DateRangeOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Stack from "@mui/material/Stack";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { setMessage, useMaterialUIController } from "context";
import { LojaPageContainer } from "../../common";
import {
  defaultFinanceiroMonthFilters,
  useFinanceiroLoja,
} from "hooks/useFinanceiroLoja";
import FinanceiroFilters from "../components/FinanceiroFilters";
import FinanceiroLancamentosGrid from "../components/FinanceiroLancamentosGrid";
import LancamentosVendasResumoCards from "../components/LancamentosVendasResumoCards";
import { FINANCEIRO_ACCENT } from "../components/financeiro-lancamentos.theme";
import { printFinanceiroLojaPdf } from "../utils/printFinanceiroPdf";

/**
 * Lançamentos de vendas — receitas e despesas gerenciais (GET /loja/financeiro/*).
 */
export default function LojaFinanceiroLancamentosVendasPage() {
  const [, dispatchCtx] = useMaterialUIController();
  const [printing, setPrinting] = useState(false);

  const {
    appliedFilters,
    pendingFilters,
    setPendingFilter,
    applyFilters,
    applyDashboardPeriodo,
    clearFilters,
    page,
    pageSize,
    setPageSize,
    rows,
    total,
    resumo,
    gridLoading,
    onPaginationModelChange,
  } = useFinanceiroLoja();

  const isFiltered = useMemo(() => {
    const defaults = defaultFinanceiroMonthFilters();
    return (
      appliedFilters.tipo !== "" ||
      appliedFilters.origem !== "" ||
      appliedFilters.status !== "" ||
      appliedFilters.pedidoId.trim() !== "" ||
      appliedFilters.pagamentoId.trim() !== "" ||
      appliedFilters.dataInicio !== defaults.dataInicio ||
      appliedFilters.dataFim !== defaults.dataFim
    );
  }, [appliedFilters]);

  const handlePrintPdf = useCallback(async () => {
    setPrinting(true);
    try {
      await printFinanceiroLojaPdf({
        resumo,
        rows,
        appliedFilters,
        page,
        pageSize,
        total,
      });
    } catch (err) {
      const text =
        err instanceof Error ? err.message : "Não foi possível gerar o PDF.";
      setMessage(dispatchCtx, { open: true, severity: "error", text });
    } finally {
      setPrinting(false);
    }
  }, [resumo, rows, appliedFilters, page, pageSize, total, dispatchCtx]);

  const handleUsarPeriodoDashboard = useCallback(() => {
    const ok = applyDashboardPeriodo();
    if (!ok) {
      setMessage(dispatchCtx, {
        open: true,
        severity: "info",
        text: "Nenhum período do dashboard encontrado. Aplique os filtros no dashboard financeiro primeiro.",
      });
    }
  }, [applyDashboardPeriodo, dispatchCtx]);

  return (
    <LojaPageContainer
      title="Lançamentos de vendas"
      subtitle="Receitas e despesas gerenciais da loja no período selecionado."
      actions={
        <Stack direction="row" spacing={1}>
          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            startIcon={<DateRangeOutlined />}
            disabled={gridLoading}
            onClick={handleUsarPeriodoDashboard}
          >
            Período do dashboard
          </MDButton>
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            disabled={gridLoading || printing}
            onClick={() => void handlePrintPdf()}
            sx={{
              background: `linear-gradient(195deg, ${FINANCEIRO_ACCENT}, #e04a1a)`,
            }}
          >
            {printing ? "Gerando…" : "Imprimir PDF"}
          </MDButton>
        </Stack>
      }
    >
      <LancamentosVendasResumoCards
        resumo={resumo}
        totalLancamentos={total}
        loading={gridLoading}
      />

      <FinanceiroFilters
        pending={pendingFilters}
        pageSize={pageSize}
        total={total}
        onChange={setPendingFilter}
        onPageSizeChange={setPageSize}
        onApply={applyFilters}
        onClear={clearFilters}
      />

      {!gridLoading && total === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <ReceiptLongOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhum lançamento encontrado
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {isFiltered
              ? "Ajuste o período ou limpe os filtros para ver outros lançamentos."
              : "Não há lançamentos registrados no período atual."}
          </MDTypography>
        </MDBox>
      ) : (
        <FinanceiroLancamentosGrid
          rows={rows}
          total={total}
          page={page}
          pageSize={pageSize}
          loading={gridLoading}
          onPaginationModelChange={onPaginationModelChange}
        />
      )}
    </LojaPageContainer>
  );
}
